/**
 * Bootstrap hook for prefetching Clients data after login/home mount
 * 
 * Goal: Prefetch the first page of Clients data in the background so that
 * when the user clicks on the Clients menu item, the data is already in cache.
 * 
 * This provides a "big CRM" experience where the Clients page appears almost instantly.
 * 
 * Usage:
 * ```tsx
 * const { company } = useParams<{ company: string }>();
 * const { client } = useClient();
 * 
 * useBootstrapClientsData({
 *   companyId: company,
 *   enabled: !!client && !!company,
 * });
 * ```
 */

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { clientKeys } from "../lib/queryKeys";
import { getAdminClients } from "../api/clientsApi";
import { DEFAULT_CLIENTS_QUERY_PARAMS } from "./clientsQueryDefaults";

type UseBootstrapClientsDataProps = {
  companyId: string | undefined;
  enabled?: boolean; // default true
};

export function useBootstrapClientsData({ 
  companyId, 
  enabled = true 
}: UseBootstrapClientsDataProps) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    if (!companyId) return;

    // Use the EXACT same default params as the Clients page initial query
    // This ensures the query keys match and cached data is immediately available
    const prefetchParams = { ...DEFAULT_CLIENTS_QUERY_PARAMS };
    const queryKey = clientKeys.list(companyId, prefetchParams);

    // Check if data already exists in cache (before prefetching)
    const existingData = queryClient.getQueryData(queryKey);
    if (existingData) {
      return;
    }

    // Prefetch in background - don't await, let it run silently
    queryClient.prefetchQuery({
      queryKey,
      queryFn: async () => {
        const response = await getAdminClients(companyId, prefetchParams);
        
        // Handle different response formats (same as useClientsQuery)
        if (Array.isArray(response)) {
          return {
            data: response,
            pagination: {
              page: 1,
              limit: response.length,
              total: response.length,
              pages: 1,
              hasMore: false,
            },
          };
        }

        if (response.data && Array.isArray(response.data)) {
          return {
            data: response.data,
            pagination: response.pagination,
            cursor: response.cursor,
          };
        }

        return {
          data: [],
          pagination: {
            page: 1,
            limit: prefetchParams.limit || 50, // Use prefetchParams.limit (should be DEFAULT_CLIENTS_PAGE_SIZE = 50)
            total: 0,
            pages: 0,
            hasMore: false,
          },
        };
      },
      // Use same caching strategy as the actual query (must match useClientsQuery)
      // Use queryOptions.heavyTable preset: 2min stale, 60min cache
      staleTime: 2 * 60 * 1000, // 2 minutes - matches queryOptions.heavyTable
      gcTime: 60 * 60 * 1000, // 60 minutes - matches queryOptions.heavyTable
    }).catch((error) => {
      // Silently handle errors - prefetch failures shouldn't break the app
    });
  }, [companyId, enabled, queryClient]);
}



